<template>
  <aside
    :class="
      cn(
        'relative flex h-screen flex-col bg-white shadow-lg transition-all duration-300',
        'fixed top-0 left-0 z-50 lg:relative lg:z-auto',
        'dark:border-r dark:border-zinc-800 dark:bg-zinc-900',
        uiStore.sidebarCollapsed ? 'w-20' : 'w-64',
        uiStore.mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      )
    "
  >
    <div
      :class="
        cn(
          'absolute top-0 bottom-0 left-0 w-1',
          authStore.currentContext === 'employee' ? 'bg-purple-600' : 'bg-green-600',
        )
      "
    ></div>

    <div class="border-b border-gray-200 px-6 py-4 dark:border-zinc-800">
      <div v-if="!uiStore.sidebarCollapsed" class="flex items-center gap-3">
        <div :class="cn('flex h-10 w-10 items-center justify-center rounded-lg', accentColors.icon)">
          <i class="pi pi-box text-xl text-white"></i>
        </div>
        <div class="flex-1">
          <router-link
            :to="homeRoute"
            class="block text-xl font-bold text-gray-800 hover:opacity-80 dark:text-gray-100"
          >
            Oxplay
          </router-link>
          <div :class="cn('mt-0.5 flex items-center gap-1.5 text-xs font-semibold', accentColors.text)">
            <i :class="authStore.currentContext === 'employee' ? 'pi pi-shield' : 'pi pi-user'"></i>
            <span>{{ authStore.currentContext === 'employee' ? 'Admin Panel' : 'Customer Panel' }}</span>
          </div>
        </div>
      </div>
      <div v-else class="flex justify-center">
        <router-link
          :to="homeRoute"
          :class="cn('flex h-10 w-10 items-center justify-center rounded-lg', accentColors.icon)"
        >
          <i class="pi pi-box text-xl text-white"></i>
        </router-link>
      </div>
    </div>

    <div
      v-if="authStore.currentContext === 'customer' && !uiStore.sidebarCollapsed"
      :class="cn('m-3 rounded-lg border p-4 shadow-sm', accentColors.border, 'bg-green-50/50')"
    >
      <div :class="cn('mb-2 flex items-center gap-2 text-xs font-bold tracking-wide uppercase', accentColors.text)">
        <i class="pi pi-building"></i>
        <span>Current Company</span>
      </div>
      <Select
        :model-value="companyStore.selectedCompanyId"
        :options="companyStore.availableCompanies"
        option-label="name"
        option-value="id"
        placeholder="Select Company"
        :filter="companyStore.availableCompanies.length > 10"
        :filter-placeholder="companyStore.availableCompanies.length > 10 ? 'Search companies...' : undefined"
        class="w-full"
        @update:model-value="handleCompanyChange"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center gap-2">
            <span class="text-base font-bold text-gray-900 dark:text-gray-100">{{
              getCompanyName(slotProps.value)
            }}</span>
          </div>
        </template>
        <template #option="slotProps">
          <div class="flex items-center justify-between gap-4">
            <span class="font-medium">{{ slotProps.option.name }}</span>
            <span
              v-if="slotProps.option.is_owner"
              :class="cn('rounded-full px-3 py-1 text-xs font-semibold text-white', accentColors.badge)"
            >
              Owner
            </span>
          </div>
        </template>
      </Select>
    </div>

    <div class="flex-1 overflow-y-auto px-3 py-4">
      <div
        v-if="!uiStore.sidebarCollapsed"
        class="mb-2 px-3 text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400"
      >
        {{ $t('menu.main_menu') }}
      </div>

      <nav class="space-y-1">
        <template v-for="item in visibleMenuItems" :key="item.id">
          <div v-if="item.items && item.items.length > 0" class="mb-4">
            <div
              v-if="!uiStore.sidebarCollapsed"
              class="mb-2 px-3 text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400"
            >
              {{ $t(item.label) }}
            </div>
            <div class="space-y-1">
              <template v-for="child in item.items" :key="child.id">
                <router-link
                  v-if="child.to"
                  v-tooltip.right="uiStore.sidebarCollapsed ? $t(child.label) : undefined"
                  :to="child.to"
                  :class="
                    cn(
                      'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      uiStore.sidebarCollapsed ? 'justify-center' : 'gap-3',
                      isActive(child.to)
                        ? cn(accentColors.bg, accentColors.text)
                        : cn(
                            'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100',
                            accentColors.hover,
                          ),
                    )
                  "
                >
                  <i v-if="child.icon" :class="child.icon"></i>
                  <span v-if="!uiStore.sidebarCollapsed" class="flex-1">{{ $t(child.label) }}</span>
                  <span
                    v-if="child.badge && !uiStore.sidebarCollapsed"
                    :class="cn('rounded-full px-2 py-0.5 text-xs text-white', accentColors.badge)"
                  >
                    {{ child.badge }}
                  </span>
                </router-link>
              </template>
            </div>
          </div>

          <router-link
            v-else-if="item.to"
            v-tooltip.right="uiStore.sidebarCollapsed ? $t(item.label) : undefined"
            :to="item.to"
            :class="
              cn(
                'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                uiStore.sidebarCollapsed ? 'justify-center' : 'gap-3',
                isActive(item.to)
                  ? cn(accentColors.bg, accentColors.text)
                  : cn(
                      'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100',
                      accentColors.hover,
                    ),
              )
            "
          >
            <i v-if="item.icon" :class="item.icon"></i>
            <span v-if="!uiStore.sidebarCollapsed" class="flex-1">{{ $t(item.label) }}</span>
            <span
              v-if="item.badge && !uiStore.sidebarCollapsed"
              :class="cn('rounded-full px-2 py-0.5 text-xs text-white', accentColors.badge)"
            >
              {{ item.badge }}
            </span>
          </router-link>
        </template>
      </nav>
    </div>

    <div class="border-t border-gray-200 dark:border-zinc-800">
      <div v-if="!uiStore.sidebarCollapsed" class="space-y-2 border-b border-gray-200 px-4 py-3 dark:border-zinc-800">
        <div class="w-full">
          <LanguageSelector class="w-full" />
        </div>
        <div class="w-full">
          <TimezoneSelector class="w-full" />
        </div>
      </div>

      <button
        v-tooltip.right="uiStore.sidebarCollapsed ? authStore.currentUser?.name || 'User' : undefined"
        :class="
          cn(
            'w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800',
            uiStore.sidebarCollapsed && 'flex justify-center',
          )
        "
        aria-label="User menu"
        @click="toggleUserMenuDropdown"
      >
        <div v-if="!uiStore.sidebarCollapsed" class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white">
            <i class="pi pi-user"></i>
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ authStore.currentUser?.name || 'User' }}
            </div>
            <div class="truncate text-xs text-gray-500 dark:text-gray-400">
              {{ authStore.currentUser?.email || '' }}
            </div>
          </div>
          <i class="pi pi-ellipsis-v text-gray-400 dark:text-gray-500"></i>
        </div>
        <div v-else class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white">
          <i class="pi pi-user"></i>
        </div>
      </button>

      <Menu ref="userMenuRef" :model="userMenuItems" popup>
        <template #item="{ item, props }">
          <div v-if="item.separator" class="my-1 border-t border-gray-200 dark:border-zinc-700"></div>
          <router-link
            v-else-if="item.to"
            v-ripple
            :to="item.to"
            class="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
            v-bind="props.action"
          >
            <i v-if="item.icon" :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </router-link>
          <a
            v-else
            v-ripple
            class="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
            v-bind="props.action"
          >
            <i v-if="item.icon" :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </a>
        </template>
      </Menu>
    </div>
  </aside>

  <div
    v-if="uiStore.mobileMenuOpen"
    class="fixed inset-0 z-40 bg-black/30 lg:hidden"
    @click="uiStore.closeMobileMenu()"
  ></div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Menu from 'primevue/menu';
import Select from 'primevue/select';

import LanguageSelector from '@/components/LanguageSelector.vue';
import TimezoneSelector from '@/components/TimezoneSelector.vue';
import { useMenu } from '@/composables/useMenu';
import { useUserMenu } from '@/composables/useUserMenu';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { useUIStore } from '@/stores/ui';
import { cn } from '@/utils/cn';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const companyStore = useCompanyStore();
const uiStore = useUIStore();
const { visibleMenuItems } = useMenu();

const userMenuRef = ref();

const homeRoute = computed(() => {
  if (authStore.currentContext === 'customer') {
    if (companyStore.selectedCompanyId) {
      return { name: 'customer.dashboard', params: { companyId: companyStore.selectedCompanyId } };
    }
    return { name: 'root' };
  }
  if (authStore.currentContext === 'employee') {
    return { name: 'admin.dashboard' };
  }
  return { name: 'root' };
});

const isActive = (itemTo: unknown): boolean => {
  if (!itemTo) {
    return false;
  }

  if (typeof itemTo === 'string') {
    return route.path === itemTo;
  }

  if (typeof itemTo === 'object' && 'name' in itemTo) {
    return String(route.name) === String(itemTo.name);
  }

  return false;
};

const handleLogout = async () => {
  const isEmployee = authStore.isEmployee;
  const isCustomer = authStore.isCustomer;

  if (authStore.currentContext === 'employee') {
    await authStore.logoutEmployee();
  } else if (authStore.currentContext === 'customer') {
    await authStore.logoutCustomer();
  } else {
    await authStore.logoutAll();
  }

  uiStore.closeMobileMenu();

  if (isEmployee && !isCustomer) {
    window.location.href = '/admin/login';
  } else if (isCustomer && !isEmployee) {
    window.location.href = '/login';
  } else {
    window.location.href = '/login';
  }
};

const { userMenuItems } = useUserMenu(handleLogout);

const getCompanyName = (id: string): string => {
  const company = companyStore.availableCompanies.find(c => c.id === id);
  return company?.name || '';
};

const handleCompanyChange = async (companyId: string) => {
  if (companyId && companyId !== companyStore.selectedCompanyId) {
    await router.push({
      name: 'customer.dashboard',
      params: { companyId },
    });
  }
};

const toggleUserMenuDropdown = (event: Event) => {
  if (userMenuRef.value && typeof userMenuRef.value.toggle === 'function') {
    userMenuRef.value.toggle(event);
  }
};

const accentColors = computed(() => {
  if (authStore.currentContext === 'employee') {
    return {
      bg: 'bg-purple-50 dark:bg-purple-900/30',
      text: 'text-purple-600 dark:text-purple-400',
      hover: 'hover:bg-purple-50',
      border: 'border-purple-200',
      badge: 'bg-purple-600',
      icon: 'bg-purple-600',
    };
  }
  return {
    bg: 'bg-green-50 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    hover: 'hover:bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-600',
    icon: 'bg-green-600',
  };
});

onMounted(async () => {
  if (authStore.currentContext === 'customer' && companyStore.availableCompanies.length === 0) {
    await companyStore.loadAvailableCompanies();
  }
});
</script>

<style scoped>
aside {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent;
}

.dark-mode aside {
  scrollbar-color: rgb(75 85 99) transparent;
}

aside::-webkit-scrollbar {
  width: 6px;
}

aside::-webkit-scrollbar-track {
  background: transparent;
}

aside::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225);
  border-radius: 3px;
}

.dark-mode aside::-webkit-scrollbar-thumb {
  background-color: rgb(75 85 99);
}

aside::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184);
}

.dark-mode aside::-webkit-scrollbar-thumb:hover {
  background-color: rgb(107 114 128);
}
</style>
